/**
 * YuNet face detection, in the browser.
 *
 * The model is face_detection_yunet_2023mar.onnx from OpenCV Zoo, served by
 * the API out of its static folder. It is 227 KB, so it costs about as much
 * as a photograph to fetch and is cached from then on.
 *
 * Nothing about a family photograph leaves the device to be detected. The
 * couple's picture is decoded, measured and cropped here; only the crops
 * they choose are ever uploaded.
 *
 * Shape of the graph, which the decoding below depends on:
 *  - one input, `input`, fixed at 1x3x640x640. onnxruntime-web cannot
 *    reshape it the way OpenCV does internally, so the image has to be
 *    letterboxed to exactly that or the session refuses it.
 *  - the blob is raw BGR, 0..255, NCHW. No mean, no scale.
 *  - twelve outputs, `cls_s`, `obj_s`, `bbox_s` and `kps_s` for each stride
 *    s in 8, 16, 32, one row per cell of that stride's feature map.
 *  - a cell's score is sqrt(cls * obj), not cls on its own.
 *  - a box's centre is (cell + p) * stride but its size is exp(p) * stride.
 *    The two forms look alike and produce plausible boxes when confused.
 */

import {
  YUNET_INPUT_SIZE, YUNET_NMS_IOU, YUNET_SCORE_MIN, YUNET_STRIDES,
} from '../../constants';

let sessionPromise = null;

/** One session for the page. Loading the graph twice buys nothing. */
const getSession = (ort, model) => {
  if (!sessionPromise) {
    // One thread: the threaded build needs cross origin isolation headers
    // that this app is not served with, and a single photograph does not
    // need the parallelism.
    ort.env.wasm.numThreads = 1;
    sessionPromise = ort.InferenceSession
      .create(model, { executionProviders: ['wasm'] })
      .catch((err) => {
        // Kept out of the cache, so a failed load can be retried.
        sessionPromise = null;
        throw err;
      });
  }
  return sessionPromise;
};

/**
 * Draw the image into the top left of a 640x640 canvas, zero padded, and
 * read it back as the raw BGR NCHW blob YuNet wants.
 *
 * Returns the blob and the scale it was drawn at, which is what turns the
 * boxes back into coordinates on the original photograph.
 */
export const letterbox = (bitmap, createCanvas) => {
  const { width, height } = bitmap;
  const scale = Math.min(YUNET_INPUT_SIZE / width, YUNET_INPUT_SIZE / height);
  const drawW = Math.max(1, Math.floor(width * scale));
  const drawH = Math.max(1, Math.floor(height * scale));

  const canvas = createCanvas(YUNET_INPUT_SIZE, YUNET_INPUT_SIZE);
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  ctx.drawImage(bitmap, 0, 0, drawW, drawH);
  const { data } = ctx.getImageData(0, 0, YUNET_INPUT_SIZE, YUNET_INPUT_SIZE);

  const plane = YUNET_INPUT_SIZE * YUNET_INPUT_SIZE;
  const blob = new Float32Array(3 * plane);
  for (let i = 0; i < plane; i += 1) {
    const at = i * 4;
    blob[i] = data[at + 2];
    blob[plane + i] = data[at + 1];
    blob[2 * plane + i] = data[at];
  }
  return { blob, scale };
};

const clamp01 = (value) => (value < 0 ? 0 : value > 1 ? 1 : value);

/** Every cell of every stride that scored well enough, in input pixels. */
export const decode = (outputs) => {
  const found = [];
  YUNET_STRIDES.forEach((stride) => {
    const cls = outputs[`cls_${stride}`].data;
    const obj = outputs[`obj_${stride}`].data;
    const box = outputs[`bbox_${stride}`].data;
    const cols = YUNET_INPUT_SIZE / stride;
    for (let i = 0; i < cls.length; i += 1) {
      const score = Math.sqrt(clamp01(cls[i]) * clamp01(obj[i]));
      if (score < YUNET_SCORE_MIN) continue;
      const at = i * 4;
      // No size floor here. A face in a group photograph is about twenty
      // pixels across once the picture is letterboxed to 640, so anything
      // measured in input pixels throws away whole back rows. How small is
      // too small is a fraction of the picture, which the server decides.
      const w = Math.exp(box[at + 2]) * stride;
      const h = Math.exp(box[at + 3]) * stride;
      const cx = ((i % cols) + box[at]) * stride;
      const cy = (Math.floor(i / cols) + box[at + 1]) * stride;
      found.push({ x: cx - w / 2, y: cy - h / 2, w, h, score });
    }
  });
  return found;
};

/** Plain IoU suppression, strongest box first, the way OpenCV does it. */
export const suppress = (boxes) => {
  const sorted = boxes.slice().sort((a, b) => b.score - a.score);
  const kept = [];
  sorted.forEach((box) => {
    const clashes = kept.some((other) => {
      const x1 = Math.max(box.x, other.x);
      const y1 = Math.max(box.y, other.y);
      const x2 = Math.min(box.x + box.w, other.x + other.w);
      const y2 = Math.min(box.y + box.h, other.y + other.h);
      const overlap = Math.max(0, x2 - x1) * Math.max(0, y2 - y1);
      if (!overlap) return false;
      const union = box.w * box.h + other.w * other.h - overlap;
      return overlap / union > YUNET_NMS_IOU;
    });
    if (!clashes) kept.push(box);
  });
  return kept;
};

/**
 * The faces on one image, as ratios of it.
 *
 * Ratios rather than pixels because that is what a face tag has always been
 * stored as, so nothing downstream has to know how large the picture was.
 */
export const facesIn = (bitmap, outputs, scale) =>
  suppress(decode(outputs)).map((box) => ({
    Left: clamp01(box.x / scale / bitmap.width),
    Top: clamp01(box.y / scale / bitmap.height),
    Width: clamp01(box.w / scale / bitmap.width),
    Height: clamp01(box.h / scale / bitmap.height),
    Confidence: box.score,
  }));

/**
 * Detect the faces on one picked file. `deps` carries the pieces this module
 * does not own, so the decoding above can be exercised without a browser.
 */
export const detectFaces = async (bitmap, { ort, model, createCanvas }) => {
  const session = await getSession(ort, model);
  const { blob, scale } = letterbox(bitmap, createCanvas);
  const input = new ort.Tensor('float32', blob, [1, 3, YUNET_INPUT_SIZE,
    YUNET_INPUT_SIZE]);
  const outputs = await session.run({ input });
  return facesIn(bitmap, outputs, scale);
};
